import { PLACEMENT_EMPLOYMENT_STATUS_OPTIONS, normalizePlacementEmploymentStatus } from '@lib/trainingPlacementImportExport';
import type { TrainingRow, C1PlacementRow } from '@type/store';

export const TRAINING_C2_ENROLLMENT_TARGET = 90000;

export function isCbtaTrainingType(type: unknown): boolean {
  const t = String(type || '').toUpperCase();
  return t.includes('CBT') || t.includes('CBTA');
}

export function trainingStats(rows: TrainingRow[]) {
  const mTotal = rows.reduce((a, r) => a + (Number(r.male) || 0), 0);
  const fTotal = rows.reduce((a, r) => a + (Number(r.female) || 0), 0);
  const tgMale = rows.reduce((a, r) => a + (Number(r.transMale) || 0), 0);
  const tgFemale = rows.reduce((a, r) => a + (Number(r.transFemale) || 0), 0);
  const enrolled = rows.reduce(
    (a, r) => a + (Number(r.total) || (Number(r.male) || 0) + (Number(r.female) || 0) + (Number(r.transMale) || 0) + (Number(r.transFemale) || 0)),
    0,
  );
  const passout = rows.reduce((a, r) => a + (Number(r.passTotal) || 0), 0);
  const inProcess = rows.filter((r) => (r.batchStatus || '').toLowerCase().includes('process')).length;
  return {
    batches: countTrainingBatchSummary(rows),
    mTotal,
    fTotal,
    tgMale,
    tgFemale,
    total: enrolled || mTotal + fTotal + tgMale + tgFemale,
    passout,
    inProcess,
    male: mTotal,
    female: fTotal,
    count: rows.length,
  };
}

export function normalizeBatchLabel(batch: unknown): string {
  const raw = String(batch ?? '').trim();
  if (!raw || raw === '—' || raw === '-') return '';
  return raw
    .toLowerCase()
    .replace(/^batch[-\s]*/i, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function resolveTrainingBatchLabel(row: TrainingRow): string {
  const fromField = normalizeBatchLabel(row.batch);
  if (fromField) return fromField;

  const sources = [row.course, row.courseName].filter(Boolean).map(String);
  for (const course of sources) {
    const dotMatch = course.match(/·\s*batch\s*(\d+|[ivx]+)/i);
    if (dotMatch) return normalizeBatchLabel(`batch-${dotMatch[1]}`);
    const suffixMatch = course.match(/\bbatch\s*(\d+|[ivx]+)\b/i);
    if (suffixMatch) return normalizeBatchLabel(`batch-${suffixMatch[1]}`);
  }
  return '';
}

export function countUniqueBatchLabels(rows: TrainingRow[]): number {
  const labels = new Set<string>();
  (rows || []).forEach((r) => {
    const label = resolveTrainingBatchLabel(r);
    if (label) labels.add(label);
  });
  return labels.size;
}

/** Unique batch cohorts in scope (Batch 1–4, Batch-I…), not courses or institutes. */
export function countTrainingBatchSummary(rows: TrainingRow[]): number {
  const all = rows || [];
  if (!all.length) return 0;

  const labelCount = countUniqueBatchLabels(all);
  if (labelCount > 0) return labelCount;

  const starts = new Set<string>();
  all.forEach((r) => {
    const start = String(r.start || '').trim();
    if (start) starts.add(start);
  });
  return starts.size;
}

export function enrichMarketTrainingRow(r: TrainingRow): TrainingRow {
  if (normalizeBatchLabel(r.batch)) return r;
  const label = resolveTrainingBatchLabel(r);
  if (!label) return r;
  return { ...r, batch: r.batch || label };
}

export function enrichMarketTrainingRows(rows: TrainingRow[]): TrainingRow[] {
  return (rows || []).map(enrichMarketTrainingRow);
}

export function c1BatchSummary(rows: TrainingRow[]) {
  const seen = new Set<string>();
  const batches: string[] = [];
  (rows || []).forEach((r) => {
    const raw = String(r.batch ?? '').trim();
    if (!raw || raw === '—' || raw === '-') return;
    const label = normalizeBatchLabel(raw);
    if (!label || seen.has(label)) return;
    seen.add(label);
    batches.push(raw);
  });
  batches.sort((a, b) => normalizeBatchLabel(a).localeCompare(normalizeBatchLabel(b)));
  const count = batches.length;
  let range = '';
  if (count === 1) range = batches[0] || '';
  else if (count > 1) range = `${count} batches`;

  return { count, range, batches };
}

export function uniqueTrainingBatchKey(
  rows: TrainingRow[],
  idx: number,
  instResolver: (rows: TrainingRow[], idx: number) => string = resolveCoeInst,
): string {
  const inst = instResolver(rows, idx);
  const batch = String(rows[idx].batch ?? '').trim().toLowerCase();
  if (batch && batch !== '—' && batch !== '-') {
    return `${normCoeInstKey(inst)}|${batch}`;
  }
  const start = String(rows[idx].start ?? '').trim();
  if (start) {
    return `${normCoeInstKey(inst)}|${start}`;
  }
  const course = normCourseKey(rows[idx].course || rows[idx].courseName);
  return `${normCoeInstKey(inst)}|${course}|${idx}`;
}

export function countUniqueTrainingBatches(
  rows: TrainingRow[],
  instResolver: (rows: TrainingRow[], idx: number) => string = resolveCoeInst,
): number {
  if (!rows.length) return 0;
  const keys = new Set<string>();
  rows.forEach((_r, i) => keys.add(uniqueTrainingBatchKey(rows, i, instResolver)));
  return keys.size;
}

export function normDistrictKey(d: unknown): string {
  let s = String(d || '').trim().toLowerCase().replace(/\./g, '').replace(/\s+/g, ' ');
  if (!s || s === '—' || s === '-' || s === 'n/a' || s === 'na') return '';
  const aliases: Record<string, string> = {
    'tt sing': 'toba tek singh',
    'toba tek sing': 'toba tek singh',
    'rahimyar khan': 'rahim yar khan',
    ryk: 'rahim yar khan',
    dgkhan: 'd g khan',
    'd g khan': 'd g khan',
  };
  return aliases[s] || s;
}

export function certifiedGraduateStats(c1Rows: TrainingRow[], c2Rows: TrainingRow[] = []) {
  let graduates = 0;
  c1Rows.forEach((r) => {
    const p = r.passTotal;
    if (p != null && p !== '' && !isNaN(Number(p))) graduates += Number(p);
  });
  const districts = new Set();
  c2Rows.forEach((r) => {
    const k = normDistrictKey(r.dist);
    if (k) districts.add(k);
  });
  return { graduates, districts: districts.size };
}

export function formatTrType(t: unknown): string {
  const s = String(t || '');
  if (!s) return '—';
  if (isCbtaTrainingType(s)) return 'CBTA';
  return 'Conv';
}

/** Component II form/storage: CBTA and CBT&A are the same; only CBTA or Conventional. */
export function normalizeC2TrainingType(type: unknown): 'CBTA' | 'Conventional' {
  return isCbtaTrainingType(type) ? 'CBTA' : 'Conventional';
}

export const COE_CUMULATIVE_THRESHOLD = 300;

export function normCoeInstKey(s: unknown): string {
  return String(s ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}

export function coeCourseName(r: TrainingRow): string {
  if (r.courseName) return r.courseName;
  return (r.course || '').replace(/ · Batch \d+$/, '');
}

export function splitCoeGender(total: number | string, maleRatio = 0.82) {
  const t = Number(total) || 0;
  if (!t) return { male: 0, female: 0 };
  const male = Math.round(t * maleRatio);
  return { male, female: t - male };
}

export function enrichCoeTrainingRow(r: TrainingRow): TrainingRow {
  let male = Number(r.male) || 0;
  let female = Number(r.female) || 0;
  const total = Number(r.total) || 0;
  if (!male && !female && total) {
    const split = splitCoeGender(total);
    male = split.male;
    female = split.female;
  }

  const passTotalRaw = r.passTotal;
  const passTotal = passTotalRaw != null && passTotalRaw !== '' ? Number(passTotalRaw) : null;
  let passMale = Number(r.passMale) || 0;
  let passFemale = Number(r.passFemale) || 0;
  const passTransMale = Number(r.passTransMale) || 0;
  const passTransFemale = Number(r.passTransFemale) || 0;
  if (passTotal && !passMale && !passFemale && !passTransMale && !passTransFemale) {
    const split = splitCoeGender(passTotal);
    passMale = split.male;
    passFemale = split.female;
  }

  let placeMale = Number(r.placeMale) || 0;
  let placeFemale = Number(r.placeFemale) || 0;
  const placeTotalRaw = r.placeTotal;
  let placeTotal = placeTotalRaw != null && placeTotalRaw !== '' ? Number(placeTotalRaw) : null;
  if (!placeTotal && (placeMale || placeFemale)) placeTotal = placeMale + placeFemale;
  if (!placeMale && !placeFemale && placeTotal) {
    const split = splitCoeGender(placeTotal);
    placeMale = split.male;
    placeFemale = split.female;
  }

  return {
    ...r,
    male,
    female,
    total: total || male + female,
    passMale: passMale || null,
    passFemale: passFemale || null,
    passTransMale: passTransMale || null,
    passTransFemale: passTransFemale || null,
    passTotal: passTotal != null && !Number.isNaN(passTotal)
      ? passTotal
      : (passMale + passFemale + passTransMale + passTransFemale) || null,
    placeMale: placeMale || null,
    placeFemale: placeFemale || null,
    placeTotal: placeTotal != null && !Number.isNaN(placeTotal) ? placeTotal : (placeMale + placeFemale) || null,
  };
}

export function enrichCoeTrainingRows(rows: TrainingRow[]): TrainingRow[] {
  return (rows || []).map(enrichCoeTrainingRow);
}

export function sortCoeTrainingRows(rows: TrainingRow[]): TrainingRow[] {
  return [...rows].sort((a, b) => {
    const ia = normCoeInstKey(a.inst);
    const ib = normCoeInstKey(b.inst);
    if (ia !== ib) return ia.localeCompare(ib);
    const ba = Number(a.batch) || 0;
    const bb = Number(b.batch) || 0;
    if (ba !== bb) return ba - bb;
    return (a.sr || 0) - (b.sr || 0);
  });
}

export interface CoeGroupMeta {
  isFirst: boolean;
  span: number;
  instResolved: string;
  cumEnrolled: number;
  cumPass: number;
  cumPlace: number;
  cumTarget: number;
  exceeds300: boolean;
  batchCount: number;
}

export function buildCoeGroupSpans(rows: TrainingRow[]): CoeGroupMeta[] {
  const meta: CoeGroupMeta[] = [];
  let i = 0;
  while (i < rows.length) {
    const inst = resolveCoeInst(rows, i);
    const key = normCoeInstKey(inst);
    let j = i + 1;
    while (j < rows.length && normCoeInstKey(resolveCoeInst(rows, j)) === key) j += 1;
    const span = j - i;
    let cumEnrolled = 0;
    let cumPass = 0;
    let cumPlace = 0;
    let cumTarget = 0;
    for (let k = i; k < j; k++) {
      const row = enrichCoeTrainingRow(rows[k]);
      cumEnrolled += Number(row.total) || 0;
      cumPass += Number(row.passTotal) || 0;
      cumPlace += Number(row.placeTotal) || 0;
      cumTarget += Number(row.target) || 0;
    }
    const exceeds300 = cumEnrolled > COE_CUMULATIVE_THRESHOLD;
    for (let k = i; k < j; k++) {
      meta[k] = {
        isFirst: k === i,
        span,
        instResolved: inst,
        cumEnrolled,
        cumPass,
        cumPlace,
        cumTarget,
        exceeds300,
        batchCount: span,
      };
    }
    i = j;
  }
  return meta;
}

export function trainingC1KpiStats(rows: TrainingRow[], placementRows: C1PlacementRow[] = []) {
  const enriched = enrichCoeTrainingRows(rows);
  const base = trainingStats(enriched);
  const passMale = enriched.reduce((a, r) => a + (Number(r.passMale) || 0), 0);
  const passFemale = enriched.reduce((a, r) => a + (Number(r.passFemale) || 0), 0);
  const passTotal = enriched.reduce((a, r) => {
    const t = Number(r.passTotal);
    if (!Number.isNaN(t) && t > 0) return a + t;
    return a + (Number(r.passMale) || 0) + (Number(r.passFemale) || 0);
  }, 0);

  const combined = c1CombinedPlacementStats(enriched, placementRows);

  const institutesTevta = new Set<string>();
  const institutesPvtc = new Set<string>();
  const institutesPrivate = new Set<string>();
  enriched.forEach((r, i) => {
    const instKey = normCoeInstKey(resolveCoeInst(enriched, i));
    if (!instKey || instKey === '—' || instKey === '-') return;
    const org = (r.org || 'TEVTA').toUpperCase();
    if (org === 'TEVTA') institutesTevta.add(instKey);
    else if (org === 'PVTC') institutesPvtc.add(instKey);
    else if (org === 'PRIVATE') institutesPrivate.add(instKey);
    else institutesTevta.add(instKey);
  });
  const coes = new Set([...institutesTevta, ...institutesPvtc, ...institutesPrivate]);
  const coesOver300 = [...coes].filter((key) => {
    const sum = enriched
      .filter((_r, i) => normCoeInstKey(resolveCoeInst(enriched, i)) === key)
      .reduce((a, r) => a + (Number(r.total) || 0), 0);
    return sum > COE_CUMULATIVE_THRESHOLD;
  }).length;
  const { count: batchCount } = c1BatchSummary(enriched);
  const rowTotal = (r: TrainingRow) => Number(r.total) || (Number(r.male) || 0) + (Number(r.female) || 0);
  const target = enriched.reduce((a, r) => a + (Number(r.target) || 0), 0);
  const enrolled = base.total;

  return {
    ...base,
    target,
    targetPct: target ? Math.min(100, (enrolled / target) * 100) : 0,
    targetRemaining: Math.max(0, target - enrolled),
    passMale,
    passFemale,
    passTotal,
    placeMale: combined.male,
    placeFemale: combined.female,
    placeTotal: combined.placed,
    passOutEligible: combined.passOutTotal,
    placementPending: combined.pending,
    proformaRecords: combined.proformaRecords,
    placementSource: combined.source,
    coes: coes.size,
    institutes: coes.size,
    institutesTevta: institutesTevta.size,
    institutesPvtc: institutesPvtc.size,
    institutesPrivate: institutesPrivate.size,
    enrolledTevta: enriched.filter((r) => (r.org || 'TEVTA').toUpperCase() === 'TEVTA').reduce((a, r) => a + rowTotal(r), 0),
    enrolledPvtc: enriched.filter((r) => (r.org || '').toUpperCase() === 'PVTC').reduce((a, r) => a + rowTotal(r), 0),
    enrolledPrivate: enriched.filter((r) => (r.org || '').toUpperCase() === 'PRIVATE').reduce((a, r) => a + rowTotal(r), 0),
    coesOver300,
    batchCount,
  };
}

export function resolveCoeInst(rows: TrainingRow[], idx: number): string {
  const inst = (rows[idx].inst || '').trim();
  if (inst && inst !== '—' && inst !== '-') return inst;
  for (let j = idx - 1; j >= 0; j--) {
    const prev = (rows[j].inst || '').trim();
    if (prev && prev !== '—' && prev !== '-') return prev;
  }
  for (let j = idx + 1; j < rows.length; j++) {
    const next = (rows[j].inst || '').trim();
    if (next && next !== '—' && next !== '-') return next;
  }
  return '—';
}

export function resolveTrainingInst(rows: TrainingRow[], idx: number): string {
  const inst = (rows[idx].inst || '').trim();
  if (inst && inst !== '—' && inst !== '-') return inst;
  for (let j = idx - 1; j >= 0; j--) {
    const p = (rows[j].inst || '').trim();
    if (p && p !== '—' && p !== '-') return p;
  }
  return '—';
}

export interface TrainingGroupMeta {
  isFirst: boolean;
  span: number;
  instResolved: string;
}

export function buildTrainingGroupSpans(rows: TrainingRow[]): TrainingGroupMeta[] {
  const meta: TrainingGroupMeta[] = [];
  let i = 0;
  while (i < rows.length) {
    const r = rows[i];
    const inst = resolveTrainingInst(rows, i);
    const key = `${r.org}|${r.div}|${r.dist}|${inst}`;
    let j = i + 1;
    while (j < rows.length) {
      const rj = rows[j];
      const instJ = resolveTrainingInst(rows, j);
      const keyJ = `${rj.org}|${rj.div}|${rj.dist}|${instJ}`;
      if (keyJ !== key) break;
      j++;
    }
    const span = j - i;
    for (let k = i; k < j; k++) meta[k] = { isFirst: k === i, span, instResolved: inst };
    i = j;
  }
  return meta;
}

export function c3DeliverableStats(d: Record<string, unknown>) {
  const rows = (d.deliverables || []) as TrainingRow[];
  const totNet = rows.reduce((a, r) => a + (Number(r.net) || 0), 0);
  const paidNet = rows.filter((r) => r.status === 'Paid').reduce((a, r) => a + (Number(r.net) || 0), 0);
  const paidPct = totNet ? Math.round((paidNet / totNet) * 100) : 0;
  return { paidPct, rows: rows.length };
}

export function trainingC2KpiStats(rows: TrainingRow[]) {
  const base = trainingStats(rows);
  const passMale = rows.reduce((a, r) => a + (Number(r.passMale) || 0), 0);
  const passFemale = rows.reduce((a, r) => a + (Number(r.passFemale) || 0), 0);
  const passTotal = rows.reduce((a, r) => {
    const total = Number(r.passTotal);
    if (!Number.isNaN(total) && total > 0) return a + total;
    return a + (Number(r.passMale) || 0) + (Number(r.passFemale) || 0);
  }, 0);

  const institutes = new Set();
  const institutesTevta = new Set();
  const institutesPvtc = new Set();
  const institutesPrivate = new Set();
  const courses = new Set();
  const coursesConv = new Set();
  const coursesCbta = new Set();

  rows.forEach((r, i) => {
    const inst = resolveTrainingInst(rows, i);
    const instKey = normTrainingInstKey(inst);
    if (instKey && instKey !== '—' && instKey !== '-') {
      institutes.add(instKey);
      const org = (r.org || '').toUpperCase();
      if (org === 'TEVTA') institutesTevta.add(instKey);
      if (org === 'PVTC') institutesPvtc.add(instKey);
      if (org === 'PRIVATE') institutesPrivate.add(instKey);
    }

    const courseKey = normCourseKey(r.course);
    if (courseKey) {
      courses.add(courseKey);
      if (isCbtaTrainingType(r.type)) coursesCbta.add(courseKey);
      else coursesConv.add(courseKey);
    }
  });

  const target = TRAINING_C2_ENROLLMENT_TARGET;
  const enrolled = base.total || 0;
  const targetPct = target ? Math.min(100, (enrolled / target) * 100) : 0;

  return {
    ...base,
    passMale,
    passFemale,
    passTotal,
    institutes: institutes.size,
    institutesTevta: institutesTevta.size,
    institutesPvtc: institutesPvtc.size,
    institutesPrivate: institutesPrivate.size,
    enrolledTevta: rows.filter((r) => (r.org || '').toUpperCase() === 'TEVTA').reduce((a, r) => a + (Number(r.total) || (Number(r.male) || 0) + (Number(r.female) || 0)), 0),
    enrolledPvtc: rows.filter((r) => (r.org || '').toUpperCase() === 'PVTC').reduce((a, r) => a + (Number(r.total) || (Number(r.male) || 0) + (Number(r.female) || 0)), 0),
    enrolledPrivate: rows.filter((r) => (r.org || '').toUpperCase() === 'PRIVATE').reduce((a, r) => a + (Number(r.total) || (Number(r.male) || 0) + (Number(r.female) || 0)), 0),
    courses: courses.size,
    coursesConv: coursesConv.size,
    coursesCbta: coursesCbta.size,
    target,
    targetPct,
    targetRemaining: Math.max(0, target - enrolled),
  };
}

export function normalizeTrainingOrgLabel(org: unknown): string {
  const raw = String(org || '').trim();
  if (!raw) return '';
  const up = raw.toUpperCase();
  if (up === 'PRIVATE') return 'Private';
  if (up === 'TEVTA') return 'TEVTA';
  if (up === 'PVTC') return 'PVTC';
  return raw;
}

export function matchesTrainingOrg(rowOrg: unknown, filterOrg: string): boolean {
  if (!filterOrg) return true;
  return normalizeTrainingOrgLabel(rowOrg).toUpperCase() === normalizeTrainingOrgLabel(filterOrg).toUpperCase();
}

export function trainingOrgFilterOptions(rows: TrainingRow[]): string[] {
  const seen = new Set<string>();
  const orgs: string[] = [];
  const add = (label: string) => {
    const key = normalizeTrainingOrgLabel(label).toUpperCase();
    if (!key || seen.has(key)) return;
    seen.add(key);
    orgs.push(normalizeTrainingOrgLabel(label));
  };
  (rows || []).forEach((r) => {
    if (r.org) add(String(r.org));
  });
  ['TEVTA', 'PVTC', 'Private'].forEach(add);
  return orgs.sort((a, b) => a.localeCompare(b));
}

export interface TrainingFilters {
  search?: string;
  org?: string;
  type?: string;
  status?: string;
  course?: string | string[];
  institute?: string | string[];
  division?: string | string[];
  district?: string | string[];
  isCoe?: boolean;
}

function asFilterKeys(value: string | string[] | undefined): string[] {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}

function matchesFilterKey(value: unknown, selected: string | string[] | undefined, normalize: (v: unknown) => string): boolean {
  const keys = asFilterKeys(selected);
  if (!keys.length) return true;
  return keys.includes(normalize(value));
}

export function displayCourseLabel(course: unknown): string {
  return String(course || '')
    .trim()
    .replace(/\s*·\s*batch\s*\d+$/i, '')
    .replace(/\s+batch\s*\d+$/i, '')
    .trim();
}

/** Normalize course name for dedup across batches (same trade = one course). */
export function normCourseKey(course: unknown): string {
  return displayCourseLabel(course)
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[^\w\s()\-/&]/g, '');
}

export function normTrainingLocationKey(value: unknown): string {
  return String(value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}

export function filterTrainingRows(rows: TrainingRow[], {
  search = '',
  org = '',
  type = '',
  status = '',
  course = '',
  institute = '',
  division = '',
  district = '',
  isCoe = false,
}: TrainingFilters = {}): TrainingRow[] {
  const q = search.toLowerCase();
  return rows.filter((r, i) => {
    const text = `${r.inst}${r.course}${r.div}${r.dist}${r.batchStatus || ''}${r.principalName || ''}${r.principalContact || ''}`.toLowerCase();
    const stOk = !isCoe || !status || (r.batchStatus || '').toLowerCase().includes(status.toLowerCase());
    const courseOk = matchesFilterKey(r.course, course, normCourseKey);
    const instName = isCoe ? resolveCoeInst(rows, i) : resolveTrainingInst(rows, i);
    const instituteOk = matchesFilterKey(instName, institute, normTrainingInstKey);
    const divisionOk = matchesFilterKey(r.div, division, normTrainingLocationKey);
    const districtOk = matchesFilterKey(r.dist, district, normTrainingLocationKey);
    return (
      stOk
      && courseOk
      && instituteOk
      && divisionOk
      && districtOk
      && (!q || text.includes(q))
      && matchesTrainingOrg(r.org, org)
      && (!type || (r.type || '').toUpperCase().includes(type.toUpperCase()))
    );
  });
}

export function trainingOrgGender(rows: TrainingRow[], org: string) {
  const filtered = rows.filter((r) => (r.org || '').toUpperCase() === org.toUpperCase());
  return {
    male: filtered.reduce((a, r) => a + (Number(r.male) || 0), 0),
    female: filtered.reduce((a, r) => a + (Number(r.female) || 0), 0),
  };
}

export function normTrainingInstKey(s: unknown): string {
  return String(s ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}

export function trainingCourseOptions(rows: TrainingRow[]) {
  const map = new Map<string, string>();
  rows.forEach((r) => {
    const key = normCourseKey(r.course);
    if (!key) return;
    const label = displayCourseLabel(r.course);
    const existing = map.get(key);
    if (!existing || label.length < existing.length) map.set(key, label);
  });
  return [...map.entries()]
    .map(([key, label]) => ({ key, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

function buildLocationOptions(rows: TrainingRow[], field: 'div' | 'dist') {
  const map = new Map<string, string>();
  rows.forEach((r) => {
    const label = String(r[field] || '').trim();
    const key = normTrainingLocationKey(label);
    if (!key || key === '—' || key === '-') return;
    if (!map.has(key)) map.set(key, label);
  });
  return [...map.entries()]
    .map(([key, label]) => ({ key, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function trainingDivisionOptions(rows: TrainingRow[]) {
  return buildLocationOptions(rows, 'div');
}

export function trainingDistrictOptions(rows: TrainingRow[]) {
  return buildLocationOptions(rows, 'dist');
}

export function buildTrainingFilterLabels(filters: {
  courses?: string[];
  institutes?: string[];
  divisions?: string[];
  districts?: string[];
  org?: string;
  type?: string;
  search?: string;
}, optionMaps: {
  courses: Array<{ key: string; label: string }>;
  institutes: Array<{ key: string; label: string }>;
  divisions: Array<{ key: string; label: string }>;
  districts: Array<{ key: string; label: string }>;
}): string[] {
  const labels: string[] = [];
  if (filters.search?.trim()) labels.push(`Search: ${filters.search.trim()}`);
  if (filters.org) labels.push(`Org: ${filters.org}`);
  if (filters.type) labels.push(`Type: ${filters.type}`);
  if (filters.courses?.length) {
    labels.push(`Courses: ${filters.courses.map((k) => optionMaps.courses.find((o) => o.key === k)?.label || k).join(', ')}`);
  }
  if (filters.institutes?.length) {
    labels.push(`Institutes: ${filters.institutes.map((k) => optionMaps.institutes.find((o) => o.key === k)?.label || k).join(', ')}`);
  }
  if (filters.divisions?.length) {
    labels.push(`Divisions: ${filters.divisions.map((k) => optionMaps.divisions.find((o) => o.key === k)?.label || k).join(', ')}`);
  }
  if (filters.districts?.length) {
    labels.push(`Districts: ${filters.districts.map((k) => optionMaps.districts.find((o) => o.key === k)?.label || k).join(', ')}`);
  }
  return labels;
}

export function trainingInstituteOptions(rows: TrainingRow[]) {
  const map = new Map();
  rows.forEach((_r, i) => {
    const inst = resolveTrainingInst(rows, i);
    const key = normTrainingInstKey(inst);
    if (!key || key === '—' || key === '-') return;
    if (!map.has(key)) map.set(key, inst);
  });
  return [...map.entries()]
    .map(([key, label]) => ({ key, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function trainingFilterSummary(
  rows: TrainingRow[],
  courseFilter: string[] = [],
  _options?: { isCoe?: boolean },
) {
  const passMale = rows.reduce((a, r) => a + (Number(r.passMale) || 0), 0);
  const passFemale = rows.reduce((a, r) => a + (Number(r.passFemale) || 0), 0);
  const passTotal = rows.reduce((a, r) => {
    const t = Number(r.passTotal);
    if (!Number.isNaN(t) && t > 0) return a + t;
    return a + (Number(r.passMale) || 0) + (Number(r.passFemale) || 0);
  }, 0);
  const enrolled = rows.reduce(
    (a, r) => a + (Number(r.total) || (Number(r.male) || 0) + (Number(r.female) || 0) + (Number(r.transMale) || 0) + (Number(r.transFemale) || 0)),
    0,
  );
  const statusCounts: Record<string, number> = {};
  rows.forEach((r) => {
    const st = String(r.batchStatus || '').trim();
    if (!st) return;
    statusCounts[st] = (statusCounts[st] || 0) + 1;
  });
  const courseTarget = courseFilter.length === 1
    ? rows.reduce((a, r) => a + (Number(r.target) || 0), 0)
    : null;
  const achieved = enrolled;
  const targetTotal = courseFilter.length === 1 && courseTarget
    ? courseTarget
    : rows.reduce((a, r) => a + (Number(r.target) || 0), 0);
  return {
    batches: countTrainingBatchSummary(rows),
    enrolled,
    passTotal,
    passMale,
    passFemale,
    mEnrolled: rows.reduce((a, r) => a + (Number(r.male) || 0), 0),
    fEnrolled: rows.reduce((a, r) => a + (Number(r.female) || 0), 0),
    tgMale: rows.reduce((a, r) => a + (Number(r.transMale) || 0), 0),
    tgFemale: rows.reduce((a, r) => a + (Number(r.transFemale) || 0), 0),
    statusCounts,
    courseTarget,
    targetTotal,
    achieved,
    targetPct: targetTotal ? Math.min(100, (achieved / targetTotal) * 100) : 0,
  };
}

export function trainingDivisionInstituteSummary(rows: TrainingRow[], divisionFilter: string[]) {
  if (!divisionFilter.length) return [];
  const filtered = rows.filter((r) => divisionFilter.includes(normTrainingLocationKey(r.div)));
  const map = new Map<string, { institute: string; enrolled: number; batchKeys: Set<string> }>();
  filtered.forEach((r, i) => {
    const inst = resolveTrainingInst(filtered, i);
    const key = normTrainingInstKey(inst);
    if (!key) return;
    const prev = map.get(key) || { institute: inst, enrolled: 0, batchKeys: new Set<string>() };
    prev.enrolled += Number(r.total) || (Number(r.male) || 0) + (Number(r.female) || 0);
    prev.batchKeys.add(uniqueTrainingBatchKey(filtered, i, resolveTrainingInst));
    map.set(key, prev);
  });
  return [...map.values()]
    .map(({ institute, enrolled, batchKeys }) => ({
      institute,
      enrolled,
      batches: batchKeys.size,
    }))
    .sort((a, b) => b.enrolled - a.enrolled);
}

export function isPlacementEmployed(status: unknown): boolean {
  const normalized = normalizePlacementEmploymentStatus(status);
  const s = normalized.toLowerCase();
  if (!s) return false;
  if (normalized === PLACEMENT_EMPLOYMENT_STATUS_OPTIONS[4]) return false;
  return PLACEMENT_EMPLOYMENT_STATUS_OPTIONS.slice(0, 4).includes(normalized as typeof PLACEMENT_EMPLOYMENT_STATUS_OPTIONS[number]);
}

export function c1PassOutTotal(r: TrainingRow): number {
  const passTotal = Number(r.passTotal);
  if (!Number.isNaN(passTotal) && passTotal > 0) return passTotal;
  return (Number(r.passMale) || 0) + (Number(r.passFemale) || 0);
}

export function c1BatchPlacementTotal(r: TrainingRow): number {
  const placeTotal = Number(r.placeTotal);
  if (!Number.isNaN(placeTotal) && placeTotal > 0) return placeTotal;
  return (Number(r.placeMale) || 0) + (Number(r.placeFemale) || 0);
}

export function c1BatchPlacementKey(r: TrainingRow, inst?: string) {
  const resolvedInst = inst || String(r.inst || '').trim();
  return [
    normCoeInstKey(resolvedInst),
    normCourseKey(r.course || r.courseName),
    String(r.batch || '').trim().toLowerCase(),
    String(r.start || '').trim(),
  ].join('|');
}

export function c1BatchPlacementStats(rows: TrainingRow[]) {
  let passOutTotal = 0;
  let passOutMale = 0;
  let passOutFemale = 0;
  let placeMale = 0;
  let placeFemale = 0;
  let placeTotal = 0;
  const batchKeys = new Set<string>();

  rows.forEach((r, i) => {
    const pass = c1PassOutTotal(r);
    if (pass <= 0) return;
    batchKeys.add(uniqueTrainingBatchKey(rows, i, resolveCoeInst));
    passOutTotal += pass;
    passOutMale += Number(r.passMale) || 0;
    passOutFemale += Number(r.passFemale) || 0;
    placeMale += Number(r.placeMale) || 0;
    placeFemale += Number(r.placeFemale) || 0;
    placeTotal += c1BatchPlacementTotal(r);
  });

  return {
    batches: batchKeys.size,
    passOutTotal,
    passOutMale,
    passOutFemale,
    placeMale,
    placeFemale,
    placeTotal,
    pending: Math.max(0, passOutTotal - Math.max(placeTotal, 0)),
  };
}

export function c1CombinedPlacementStats(enrolmentRows: TrainingRow[], placementRows: C1PlacementRow[] = []) {
  const batch = c1BatchPlacementStats(enrolmentRows);
  const proforma = placementC1Stats(placementRows);
  const hasProformaPlaced = proforma.placed > 0;

  if (hasProformaPlaced) {
    return {
      male: proforma.male,
      female: proforma.female,
      placed: proforma.placed,
      passOutTotal: batch.passOutTotal,
      pending: Math.max(0, batch.passOutTotal - proforma.placed),
      proformaRecords: placementRows.length,
      source: 'proforma' as const,
    };
  }

  return {
    male: batch.placeMale,
    female: batch.placeFemale,
    placed: batch.placeTotal,
    passOutTotal: batch.passOutTotal,
    pending: Math.max(0, batch.passOutTotal - batch.placeTotal),
    proformaRecords: placementRows.length,
    source: 'batch' as const,
  };
}

export interface C1BatchPlacementSummaryRow {
  key: string;
  div: string;
  dist: string;
  inst: string;
  course: string;
  batch: string;
  dur: string;
  start: string;
  end: string;
  passMale: number;
  passFemale: number;
  passTotal: number;
  placeMale: number;
  placeFemale: number;
  placeTotal: number;
  proformaCount: number;
  pending: number;
}

export function c1BatchPlacementSummaryRows(
  rows: TrainingRow[],
  placementRows: C1PlacementRow[] = [],
): C1BatchPlacementSummaryRow[] {
  const enriched = enrichCoeTrainingRows(rows);
  const proformaByBatch = new Map<string, number>();

  placementRows.forEach((row) => {
    const pseudo: TrainingRow = {
      inst: row.inst,
      course: row.course,
      batch: '',
      start: row.start,
    };
    const key = c1BatchPlacementKey(pseudo, String(row.inst || ''));
    proformaByBatch.set(key, (proformaByBatch.get(key) || 0) + 1);
  });

  return enriched
    .map((r, i) => {
      const passTotal = c1PassOutTotal(r);
      if (passTotal <= 0) return null;
      const inst = resolveCoeInst(enriched, i);
      const key = c1BatchPlacementKey(r, inst);
      const placeTotal = c1BatchPlacementTotal(r);
      const proformaCount = proformaByBatch.get(key) || 0;
      const tracked = Math.max(placeTotal, proformaCount);
      return {
        key,
        div: String(r.div || ''),
        dist: String(r.dist || ''),
        inst,
        course: coeCourseName(r),
        batch: String(r.batch || '—'),
        dur: String(r.dur || ''),
        start: String(r.start || ''),
        end: String(r.end || ''),
        passMale: Number(r.passMale) || 0,
        passFemale: Number(r.passFemale) || 0,
        passTotal,
        placeMale: Number(r.placeMale) || 0,
        placeFemale: Number(r.placeFemale) || 0,
        placeTotal,
        proformaCount,
        pending: Math.max(0, passTotal - tracked),
      };
    })
    .filter((row): row is C1BatchPlacementSummaryRow => row != null);
}

export function filterC1BatchPlacementSummary(
  rows: C1BatchPlacementSummaryRow[],
  filters: {
    search?: string;
    course?: string[];
    institute?: string[];
    division?: string[];
    district?: string[];
  },
) {
  const pseudoRows = rows.map((r) => ({
    inst: r.inst,
    course: r.course,
    div: r.div,
    dist: r.dist,
  })) as TrainingRow[];

  return rows.filter((_row, i) => {
    const matchRow = filterTrainingRows([pseudoRows[i]], {
      search: filters.search,
      course: filters.course,
      institute: filters.institute,
      division: filters.division,
      district: filters.district,
      isCoe: true,
    });
    return matchRow.length > 0;
  });
}

export function c1BatchPlacementSummaryStats(
  rows: C1BatchPlacementSummaryRow[],
  options?: { batchCount?: number },
) {
  const batchKeys = new Set<string>();
  const totals = rows.reduce((acc, row) => {
    const label = normalizeBatchLabel(row.batch);
    if (label) batchKeys.add(label);
    return {
      passOutTotal: acc.passOutTotal + row.passTotal,
      placed: acc.placed + Math.max(row.placeTotal, row.proformaCount),
      pending: acc.pending + row.pending,
      proformaRecords: acc.proformaRecords + row.proformaCount,
    };
  }, {
    passOutTotal: 0,
    placed: 0,
    pending: 0,
    proformaRecords: 0,
  });

  return {
    ...totals,
    batches: options?.batchCount ?? batchKeys.size,
  };
}

export function placementC1Stats(rows: C1PlacementRow[]) {
  let male = 0;
  let female = 0;
  let placed = 0;
  const statusCounts: Record<string, number> = {};
  rows.forEach((r) => {
    const normalized = normalizePlacementEmploymentStatus(r.employmentStatus);
    const status = PLACEMENT_EMPLOYMENT_STATUS_OPTIONS.includes(
      normalized as typeof PLACEMENT_EMPLOYMENT_STATUS_OPTIONS[number],
    )
      ? normalized
      : (normalized ? 'Unspecified' : 'Unspecified');
    statusCounts[status] = (statusCounts[status] || 0) + 1;
    if (!isPlacementEmployed(r.employmentStatus)) return;
    placed += 1;
    const g = String(r.gender || '').trim().toUpperCase();
    if (g.startsWith('M')) male += 1;
    else if (g.startsWith('F')) female += 1;
  });
  return { male, female, placed, total: rows.length, statusCounts };
}

export function placementStatusBreakdown(rows: C1PlacementRow[] = []) {
  const { statusCounts } = placementC1Stats(rows);
  const known = PLACEMENT_EMPLOYMENT_STATUS_OPTIONS.map((status) => ({
    status,
    count: statusCounts[status] || 0,
  }));
  const unspecified = statusCounts.Unspecified || 0;
  return unspecified > 0
    ? [...known, { status: 'Unspecified', count: unspecified }]
    : known;
}

export function filterPlacementRows(rows: C1PlacementRow[], filters: {
  search?: string;
  org?: string;
  course?: string[];
  institute?: string[];
  division?: string[];
  district?: string[];
}) {
  return filterTrainingRows(rows as TrainingRow[], {
    search: filters.search,
    org: filters.org,
    course: filters.course,
    institute: filters.institute,
    division: filters.division,
    district: filters.district,
  }) as C1PlacementRow[];
}

export function placementCourseOptions(rows: C1PlacementRow[]) {
  return trainingCourseOptions(rows as TrainingRow[]);
}

export function placementInstituteOptions(rows: C1PlacementRow[]) {
  return trainingInstituteOptions(rows as TrainingRow[]);
}

export function placementDivisionOptions(rows: C1PlacementRow[]) {
  return trainingDivisionOptions(rows as TrainingRow[]);
}

export function placementDistrictOptions(rows: C1PlacementRow[]) {
  return trainingDistrictOptions(rows as TrainingRow[]);
}

export function buildPrincipalMap(principals: Record<string, { name?: string; contact?: string }> | null | undefined) {
  const pmap: Record<string, { name?: string; contact?: string }> = {};
  Object.entries(principals || {}).forEach(([k, v]) => {
    pmap[normTrainingInstKey(k)] = v;
  });
  return pmap;
}

export function lookupPrincipal(
  rows: TrainingRow[],
  idx: number,
  principals: Record<string, { name?: string; contact?: string }> | null | undefined,
) {
  const pmap = buildPrincipalMap(principals);
  const inst = resolveTrainingInst(rows, idx);
  const p = pmap[normTrainingInstKey(inst)];
  const row = rows[idx] || {};
  if (p) return { name: p.name, contact: p.contact };
  return {
    name: row.principalName != null && row.principalName !== '' ? row.principalName : '—',
    contact: row.principalContact != null && row.principalContact !== '' ? row.principalContact : '—',
  };
}

export function enrichTrainingPrincipals(
  rows: TrainingRow[],
  principals: Record<string, { name?: string; contact?: string }> | null | undefined,
): TrainingRow[] {
  const pmap = buildPrincipalMap(principals);
  return rows.map((r, i) => {
    const inst = resolveTrainingInst(rows, i);
    const p = pmap[normTrainingInstKey(inst)];
    if (p) {
      return { ...r, principalName: p.name, principalContact: p.contact };
    }
    return {
      ...r,
      principalName: r.principalName == null || r.principalName === '' ? '—' : r.principalName,
      principalContact: r.principalContact == null || r.principalContact === '' ? '—' : r.principalContact,
    };
  });
}
